import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { toast } from 'react-hot-toast'
import { Link } from 'react-router-dom'
import { Megaphone, Plus, Rocket, Users, Ban, X, ChevronRight, ChevronLeft, CheckCircle2 } from 'lucide-react'
import { getSession } from '../lib/session'
import AgentAsk from '../components/AgentAsk'

// Broadcast campaigns: a template + an opted-in audience segment, launched to the
// Broadcast Agent. Each row shows a live sent/delivered/read/failed breakdown that
// fills in as the worker sends.
const STATUS_CLS = {
  draft:     'bg-gray-100 text-gray-500',
  scheduled: 'bg-blue-500/10 text-blue-600',
  queued:    'bg-amber-500/10 text-amber-600',
  sending:   'bg-amber-500/10 text-amber-600',
  sent:      'bg-green-500/10 text-green-600',
  cancelled: 'bg-gray-100 text-gray-400',
}

export default function Campaigns() {
  const session = getSession()
  const [campaigns, setCampaigns] = useState([])
  const [stats, setStats] = useState({}) // id -> {total,sent,delivered,read,failed}
  const [loading, setLoading] = useState(false)
  const [busyId, setBusyId] = useState('')
  const [showWizard, setShowWizard] = useState(false)

  const load = () => {
    if (!session?.namespaceId) return
    setLoading(true)
    axios.get('/api/v1/campaigns')
      .then(r => {
        const list = r.data.campaigns || []
        setCampaigns(list)
        // Pull live stats for any campaign that has started sending.
        list.filter(c => ['queued', 'sending', 'sent'].includes(c.status)).forEach(c => {
          axios.get(`/api/v1/campaigns/${c.id}/stats`).then(s => setStats(m => ({ ...m, [c.id]: s.data }))).catch(() => {})
        })
      })
      .catch(err => toast.error(err.response?.data?.error || 'Failed to load campaigns'))
      .finally(() => setLoading(false))
  }
  useEffect(load, [session?.namespaceId])

  // Poll live stats while anything is actively sending.
  useEffect(() => {
    const active = campaigns.some(c => ['queued', 'sending'].includes(c.status))
    if (!active) return
    const id = setInterval(() => {
      campaigns.filter(c => ['queued', 'sending'].includes(c.status)).forEach(c => {
        axios.get(`/api/v1/campaigns/${c.id}/stats`).then(s => setStats(m => ({ ...m, [c.id]: s.data }))).catch(() => {})
      })
    }, 5000)
    return () => clearInterval(id)
  }, [campaigns])

  const launch = async (c) => {
    setBusyId(c.id)
    try {
      const r = await axios.post(`/api/v1/campaigns/${c.id}/launch`)
      toast.success(`Launched to ${r.data.recipients} recipient${r.data.recipients === 1 ? '' : 's'}`)
      load()
    } catch (err) { toast.error(err.response?.data?.error || 'Launch failed') }
    finally { setBusyId('') }
  }

  const cancel = async (c) => {
    setBusyId(c.id)
    try { await axios.post(`/api/v1/campaigns/${c.id}/cancel`); load() }
    catch (err) { toast.error(err.response?.data?.error || 'Cancel failed') }
    finally { setBusyId('') }
  }

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Megaphone size={24} className="text-primary-600" /> Campaigns</h2>
          <p className="text-gray-500 mt-1">Broadcast an approved template to a segment of your opted-in contacts.</p>
        </div>
        <button onClick={() => setShowWizard(true)} className="btn-primary text-sm flex items-center gap-2"><Plus size={16} /> New campaign</button>
      </div>

      <div className="max-w-2xl"><AgentAsk service="broadcast" agentName="Broadcast Agent" starters={['How did my last campaign perform?', 'How many opted-in contacts do I have?']} /></div>

      {loading && <div className="card text-center text-gray-400 py-10">Loading…</div>}
      {!loading && campaigns.length === 0 && (
        <div className="card text-center py-12">
          <Megaphone size={32} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500">No campaigns yet. Create one to broadcast to your audience.</p>
        </div>
      )}

      <div className="space-y-4">
        {campaigns.map(c => {
          const s = stats[c.id] || c.stats || { total: c.total_recipients || 0, sent: 0, delivered: 0, read: 0, failed: 0 }
          return (
            <div key={c.id} className="card">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900">{c.name}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{c.template_name} · {(c.total_recipients || s.total || 0).toLocaleString()} recipients</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full capitalize ${STATUS_CLS[c.status] || 'bg-gray-100 text-gray-500'}`}>{c.status}</span>
                  {c.status === 'draft' && (
                    <button onClick={() => launch(c)} disabled={busyId === c.id}
                      className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 disabled:opacity-60"><Rocket size={14} /> Launch</button>
                  )}
                  {['draft', 'scheduled'].includes(c.status) && (
                    <button onClick={() => cancel(c)} disabled={busyId === c.id}
                      className="text-sm text-gray-400 hover:text-red-600 flex items-center gap-1 disabled:opacity-60"><Ban size={14} /> Cancel</button>
                  )}
                </div>
              </div>
              <StatsBar s={s} />
            </div>
          )
        })}
      </div>

      {showWizard && <CampaignWizard onClose={() => setShowWizard(false)} onDone={() => { setShowWizard(false); load() }} />}
    </div>
  )
}

// Proportional sent/delivered/read/failed bar with a legend beneath.
function StatsBar({ s }) {
  const total = Math.max(s.total || 0, (s.sent || 0) + (s.delivered || 0) + (s.read || 0) + (s.failed || 0), 1)
  const segs = [
    { key: 'read', label: 'Read', value: s.read || 0, bar: 'bg-green-500', dot: 'bg-green-500' },
    { key: 'delivered', label: 'Delivered', value: s.delivered || 0, bar: 'bg-primary-500', dot: 'bg-primary-500' },
    { key: 'sent', label: 'Sent', value: s.sent || 0, bar: 'bg-blue-400', dot: 'bg-blue-400' },
    { key: 'failed', label: 'Failed', value: s.failed || 0, bar: 'bg-red-500', dot: 'bg-red-500' },
  ]
  return (
    <div className="mt-4">
      <div className="flex h-2.5 w-full rounded-full bg-gray-100 overflow-hidden">
        {segs.map(seg => seg.value > 0 && (
          <div key={seg.key} className={`h-full ${seg.bar}`} style={{ width: `${(seg.value / total) * 100}%` }} title={`${seg.label}: ${seg.value}`} />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-gray-500">
        {segs.map(seg => (
          <span key={seg.key} className="flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${seg.dot}`} /> {seg.label} <span className="font-medium text-gray-700 tabular-nums">{seg.value.toLocaleString()}</span>
          </span>
        ))}
      </div>
    </div>
  )
}

function CampaignWizard({ onClose, onDone }) {
  const [step, setStep] = useState(1)
  const [templates, setTemplates] = useState([])
  const [name, setName] = useState('')
  const [templateId, setTemplateId] = useState('')
  const [tags, setTags] = useState('')
  const [match, setMatch] = useState('any') // any | all
  const [count, setCount] = useState(null)
  const [previewing, setPreviewing] = useState(false)
  const [launching, setLaunching] = useState(false)

  useEffect(() => {
    axios.get('/api/v1/campaigns/templates').then(r => setTemplates((r.data.templates || []).filter(t => t.status === 'APPROVED'))).catch(() => {})
  }, [])

  const tagList = () => tags.split(',').map(t => t.trim()).filter(Boolean)
  const audienceFilter = () => ({ tags: tagList(), match, opt_in_only: true })

  const preview = async () => {
    setPreviewing(true)
    try {
      const r = await axios.post('/api/v1/campaigns/audience/preview', audienceFilter())
      setCount(r.data.count)
    } catch (err) { toast.error(err.response?.data?.error || 'Preview failed') }
    finally { setPreviewing(false) }
  }
  // Recompute the segment size whenever the audience changes on step 2.
  useEffect(() => { if (step === 2) preview() }, [step, tags, match])

  const launch = async () => {
    if (!name.trim()) { toast.error('Name your campaign'); return }
    if (!templateId) { toast.error('Pick a template'); return }
    setLaunching(true)
    try {
      const created = await axios.post('/api/v1/campaigns', {
        name: name.trim(), template_id: templateId, audience_filter: audienceFilter(),
      }, { validateStatus: s => s < 500 })
      if (created.status >= 400) throw new Error(created.data.error)
      const r = await axios.post(`/api/v1/campaigns/${created.data.id}/launch`)
      toast.success(`Launched to ${r.data.recipients} recipient${r.data.recipients === 1 ? '' : 's'}`)
      onDone()
    } catch (err) { toast.error(err.response?.data?.error || err.message || 'Could not launch') }
    finally { setLaunching(false) }
  }

  const chosenTemplate = templates.find(t => t.id === templateId)

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-gray-100 border border-gray-200 rounded-xl w-full max-w-lg p-6 max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-semibold text-gray-900">New campaign</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        {/* Stepper */}
        <div className="flex items-center gap-2 mb-5 text-xs">
          {['Template', 'Audience', 'Review'].map((label, i) => (
            <React.Fragment key={label}>
              <span className={`flex items-center gap-1.5 ${step === i + 1 ? 'text-primary-600 font-semibold' : step > i + 1 ? 'text-green-600' : 'text-gray-400'}`}>
                {step > i + 1 ? <CheckCircle2 size={14} /> : <span className={`w-4 h-4 rounded-full text-[10px] flex items-center justify-center ${step === i + 1 ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-500'}`}>{i + 1}</span>}
                {label}
              </span>
              {i < 2 && <span className="text-gray-300">—</span>}
            </React.Fragment>
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-3">
            <label className="block"><span className="block text-xs font-medium text-gray-500 mb-1">Campaign name</span>
              <input className="input" value={name} onChange={e => setName(e.target.value)} placeholder="August promo" /></label>
            <div>
              <span className="block text-xs font-medium text-gray-500 mb-1">Template (approved only)</span>
              {templates.length === 0 ? (
                <p className="text-sm text-gray-400 rounded-lg border border-gray-200 bg-gray-50 p-3">
                  No approved templates yet. <Link to="/templates" className="text-primary-600 hover:underline">Create and get one approved →</Link>
                </p>
              ) : (
                <div className="space-y-2 max-h-52 overflow-auto">
                  {templates.map(t => (
                    <button key={t.id} onClick={() => setTemplateId(t.id)}
                      className={`w-full text-left rounded-lg border p-3 transition-colors ${templateId === t.id ? 'border-primary-600 bg-primary-500/5' : 'border-gray-200 hover:bg-gray-50'}`}>
                      <p className="font-medium text-sm text-gray-900">{t.name}</p>
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{t.body_text}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            <label className="block"><span className="block text-xs font-medium text-gray-500 mb-1">Audience tags (comma-separated — leave blank for all opted-in)</span>
              <input className="input" value={tags} onChange={e => setTags(e.target.value)} placeholder="vip, lead" /></label>
            <div>
              <span className="block text-xs font-medium text-gray-500 mb-1">Match</span>
              <div className="flex gap-2">
                {[['any', 'Any tag'], ['all', 'All tags']].map(([v, l]) => (
                  <button key={v} onClick={() => setMatch(v)}
                    className={`px-3 py-1.5 rounded-lg text-sm border ${match === v ? 'border-primary-600 text-primary-600 bg-primary-500/5' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>{l}</button>
                ))}
              </div>
            </div>
            <div className="rounded-lg bg-primary-500/5 border border-primary-600/20 p-4 flex items-center gap-3">
              <Users size={20} className="text-primary-600" />
              <div>
                <p className="text-sm text-gray-600">This campaign will reach</p>
                <p className="text-xl font-bold text-gray-900">{previewing ? '…' : (count ?? 0).toLocaleString()} opted-in contact{count === 1 ? '' : 's'}</p>
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3 text-sm">
            <Row label="Campaign" value={name || '—'} />
            <Row label="Template" value={chosenTemplate?.name || '—'} />
            <Row label="Audience" value={tagList().length ? `${tagList().join(', ')} (${match})` : 'All opted-in'} />
            <Row label="Recipients" value={`${(count ?? 0).toLocaleString()} opted-in`} />
            <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
              <p className="text-xs text-gray-400 mb-1">Message preview</p>
              {chosenTemplate?.header_text && <p className="font-semibold text-gray-800">{chosenTemplate.header_text}</p>}
              <p className="text-gray-700 whitespace-pre-wrap">{chosenTemplate?.body_text}</p>
              {chosenTemplate?.footer_text && <p className="text-xs text-gray-400 mt-1">{chosenTemplate.footer_text}</p>}
            </div>
          </div>
        )}

        {/* Footer nav */}
        <div className="flex items-center justify-between mt-6">
          <button onClick={() => step === 1 ? onClose() : setStep(step - 1)} className="btn-secondary text-sm flex items-center gap-1">
            {step === 1 ? 'Cancel' : <><ChevronLeft size={15} /> Back</>}
          </button>
          {step < 3 ? (
            <button onClick={() => setStep(step + 1)} disabled={(step === 1 && (!name.trim() || !templateId))}
              className="btn-primary text-sm flex items-center gap-1 disabled:opacity-60">Next <ChevronRight size={15} /></button>
          ) : (
            <button onClick={launch} disabled={launching || !count} className="btn-primary text-sm flex items-center gap-1 disabled:opacity-60">
              <Rocket size={15} /> {launching ? 'Launching…' : 'Launch campaign'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-gray-100 pb-2 last:border-0">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium text-gray-900 text-right">{value}</span>
    </div>
  )
}
